'use client';

/**
 * `useLobbyBoards()` — single source of truth for the boards lobby.
 *
 * Fetches the live grid list via the SDK's `useGrids()` hook and maps
 * each row through `gridSummaryToMockBoard()` so the rest of the lobby
 * (cards, canvas theming, panels, action sheet) keeps consuming the
 * familiar `MockBoard` shape.
 *
 * The flagship **Boardroom** is HV grid 0 (env-driven via
 * `NEXT_PUBLIC_HV_*`). It's surfaced through the same `useGrids()`
 * response as every community board but peeled out into the `pinned`
 * slot with the canonical Boardroom branding (name, skybox, plinth,
 * hue, description) overlaid on top of the live counts. When the
 * live grid hasn't loaded yet, or HV env isn't wired (early SSR /
 * unit tests), we fall back to the static `MockBoard` stub so the
 * pinned card never disappears.
 *
 * Returned slots:
 *   • `pinned`     — Boardroom (live + branded overlay or static stub)
 *   • `curated`    — DB-backed allow-list, populated in Phase 4
 *   • `community`  — every other live grid the gateway exposes
 *   • `dev`        — reserved for future dev-only builds; always empty
 *   • `byId`       — flat lookup of every visible board, used by
 *                    `LobbyClient` to resolve `activeBoardId`
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGrids } from '@takeover/sdk/client';
import {
  MOCK_BOARDS,
  isBoardNew,
  REWARDS_PENDING_USD,
  type MockBoard,
} from '@/features/lobby/data/mock-boards';
import { gridSummaryToMockBoard, formatYield7dFromRaw } from '@/features/lobby/data/grid-to-board';
import { gridIdToPath } from '@/lib/utils';
import {
  BOARDROOM_BOARD_ID,
  getBoardroomLiveGridId,
} from '@/features/lobby/data/boardroom-grid';
import { useNowSec } from './use-now-sec';
import { useTakeoverPrice } from './use-takeover-price';
import { useEthPrice } from './use-eth-price';

export interface UseLobbyBoardsResult {
  /** Pinned boardroom card — live data when available, mock stub
   *  otherwise. Always exactly one entry today. */
  pinned: MockBoard[];
  /** DB-backed curated cards. Empty until Phase 4. */
  curated: MockBoard[];
  /** Every live grid exposed by `/api/v1/grids`, minus the boardroom
   *  (which is rendered in the `pinned` slot instead). */
  community: MockBoard[];
  /** Reserved for future dev-only scaffolding — always empty today. */
  dev: MockBoard[];
  /** Flat lookup of every visible board for `activeBoardId` resolution. */
  byId: Map<string, MockBoard>;
  /** All visible boards in render order (pinned → curated → community → dev). */
  all: MockBoard[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Take a raw `LobbyGridSummary` for HV grid 0 and dress it in the
 * canonical Boardroom branding. Live counts (capacity / taken / floor /
 * rewards / TVL / tax+yield tokens / age) come straight from the
 * mapper; visual + descriptive fields override the defaults so the
 * card always reads as "the Boardroom" regardless of any onchain
 * metadata changes.
 *
 * The lobby keys boards by `id`, so the resulting overlay keeps
 * `BOARDROOM_BOARD_ID` ('boardroom') as its id — the data hooks
 * (`useLobbyGridPage`, `useLobbySeats`, etc.) translate that back to
 * the live composite id when wiring SSE / seats.
 */
function brandBoardroomOverlay(base: MockBoard, live: MockBoard): MockBoard {
  return {
    // The Boardroom's *identity* fields stay frozen on the static brand
    // (name, skybox, plinth, hue, socials) — those are the "this stays
    // the Boardroom" bits that shouldn't be overwritten by a name/theme
    // edit on HV grid 0. Description + tagline are intentionally *not*
    // frozen below: the description tracks the board's
    // `pool_metadata.description` so editing the info card on the board
    // page propagates to the lobby tooltip, and the tagline pulls the
    // live "X / Y seats" string from `taglineFromGrid` so the pinned
    // card matches the same seat-count summary every community card uses.
    ...base,
    // …but counts, prices, tax/yield tokens and freshness come from
    // the onchain summary so the rail card always tracks reality.
    tagline: live.tagline,
    description: live.description,
    // Image now follows the board's live metadata (`pool_metadata.image`,
    // resolved into `g.imageUrl`) rather than the static `/boardroom.png`
    // brand asset, which was removed — falling through to `base.imageUrl`
    // only when the grid has no metadata image yet. `BoardroomCard`
    // renders it via `BoardCover`, which degrades to a procedural mosaic
    // if the URL fails.
    imageUrl: live.imageUrl ?? base.imageUrl,
    gridSize: live.gridSize,
    capacity: live.capacity,
    taken: live.taken,
    taxToken: live.taxToken,
    yieldToken: live.yieldToken,
    floor: live.floor,
    rewards7d: live.rewards7d,
    taxesUsd: live.taxesUsd,
    tvl: live.tvl,
    ageDays: live.ageDays,
    // Forward the activity-sort inputs from the live row so the
    // pinned Boardroom card participates in the same sort comparator
    // as community boards (it doesn't actually compete on the sort —
    // it's pinned — but the fields are part of the `MockBoard`
    // contract and downstream code shouldn't see them frozen at
    // their static-stub `0`/`null` defaults). The chat count in
    // particular needs to come through live since the Boardroom is
    // far and away the most active room.
    uniqueHolderCount: live.uniqueHolderCount,
    lastSeatPurchasedAtSec: live.lastSeatPurchasedAtSec,
    chatMessages7d: live.chatMessages7d,
    // Pull the precise creation timestamp through to the branded
    // overlay so any consumer that asks "is this board fresh?" via
    // `isBoardNew` gets the real chain timestamp instead of the
    // static fallback's `0`. Boardroom is months old in practice so
    // it never trips the 4h NEW window — but threading the field
    // through keeps `MockBoard` invariants consistent and means a
    // brand-new flagship deployment (e.g. a chain swap) lights the
    // pill correctly without a special case.
    createdAtSec: live.createdAtSec,
    pinned: true,
    premium: true,
  };
}

export function useLobbyBoards(
  options?: { limit?: number },
): UseLobbyBoardsResult {
  // Default 100 keeps the lobby picker's existing cap; the marketing
  // landing passes a higher limit (the API caps at 250) so its
  // aggregate stats / leaderboard cover every live board rather than
  // the first page. Distinct limits are distinct react-query keys, so
  // the lobby and landing don't collide.
  const { data: rawLiveGrids = [], isLoading, isError } = useGrids({
    curation: 'all',
    limit: options?.limit ?? 100,
  });

  // `/api/v1/grids` no longer drops banned rows server-side — every
  // banned grid is returned with `banned: true` so SDK builders can
  // decide their own moderation UX. The takeover-app's lobby keeps
  // the prior "banned boards are hidden" behaviour by filtering
  // here, *before* the boardroom-overlay / curated-or-fresh split,
  // so a banned grid never flickers into the curated rail or the
  // pinned slot. Per-board pages still resolve via the grid
  // endpoint (which also stamps `banned: true`) so seat holders can
  // manage their positions and the `BoardBannedBanner` shows.
  const liveGrids = useMemo(
    () => rawLiveGrids.filter((g) => !g.banned),
    [rawLiveGrids],
  );

  // Live USD prices for non-USDC yield tokens (TAKEOVER, ETH/WETH).
  // Threaded through `gridSummaryToMockBoard` so rail cards display
  // real $-figures rather than the stale `MOCK_*` constants in
  // `mock-prices.ts`.
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const { ethPrice } = useEthPrice();

  // Single shared "now" used to bucket boards into curated vs.
  // community. Ticks once a minute so a board that was 3h59m old when
  // the lobby mounted will fall out of the curated rail at the 4h mark
  // even if no new live-grid data has streamed in (e.g. the user has
  // the lobby open in a background tab) — `useGrids()` does not poll
  // by default. Keeping the recomputation cadence at 60s is far below
  // the 4h window so the boundary feels precise without thrashing the
  // memo.
  const nowSec = useNowSec(60_000);

  // Boardroom-only accurate 7D rewards. The grid-level `yield7d` rollup
  // undercounts the boardroom specifically: Land-Protection / keeper-fee
  // reward rows flush into it and are counted in `GridRewardEvent` (what the
  // activity-panel chart sums) but not in the rollup. Community boards have
  // no such side-channel, so their rollup is accurate — we only need this
  // one extra (cached) query for the pinned card, summing the same
  // `rewards-chart` 7d window the panel uses. Result is the raw token sum;
  // the USD conversion happens below with the live summary's decimals/token.
  const hvGridId = getBoardroomLiveGridId();
  const {
    data: boardroomRewards7dRaw,
    isError: boardroomRewardsError,
  } = useQuery({
    queryKey: ['boardroom-rewards-7d', hvGridId],
    enabled: !!hvGridId,
    staleTime: 60_000,
    refetchInterval: 120_000,
    queryFn: async (): Promise<string> => {
      const res = await fetch(`/api/grid/${gridIdToPath(hvGridId!)}/rewards-chart?window=7d`);
      if (!res.ok) throw new Error(`rewards-chart ${res.status}`);
      const json = await res.json();
      const buckets = json?.data?.buckets;
      if (!Array.isArray(buckets)) return '0';
      let sum = 0n;
      for (const b of buckets) {
        try {
          sum += BigInt(b.value);
        } catch {
          /* skip a malformed bucket rather than fail the whole sum */
        }
      }
      return sum.toString();
    },
  });

  return useMemo(() => {
    const boardroomLiveId = getBoardroomLiveGridId();
    const dev: MockBoard[] = [];
    const prices = { takeoverUsd: takeoverPriceUsd, ethUsd: ethPrice };

    // Static Boardroom shell — used as the live-data fallback so the
    // pinned card never disappears mid-load.
    const boardroomFallback = MOCK_BOARDS.find((b) => b.id === BOARDROOM_BOARD_ID);

    // Find the live boardroom row, if any. Composite ids are stored
    // lowercased in the live response; HV_TAKEOVER_GRID_ID is also
    // lowercased so the comparison is direct.
    const liveBoardroom = boardroomLiveId
      ? liveGrids.find((g) => g.id.toLowerCase() === boardroomLiveId.toLowerCase())
      : undefined;

    const pinned: MockBoard[] = [];
    if (liveBoardroom && boardroomFallback) {
      const base = brandBoardroomOverlay(boardroomFallback, gridSummaryToMockBoard(liveBoardroom, prices));
      // Override the (undercounting) rollup `rewards7d` with the live
      // GridRewardEvent 7d sum, converted with the live summary's token +
      // decimals. While the query is in flight → pending sentinel (card
      // shows a skeleton, never the static fallback figure). On fetch error
      // → fall back to the rollup value rather than hang on a skeleton.
      const liveRewards7d =
        typeof boardroomRewards7dRaw === 'string'
          ? formatYield7dFromRaw(
              boardroomRewards7dRaw,
              liveBoardroom.yieldToken,
              liveBoardroom.yieldTokenDecimals,
              prices,
            )
          : null;
      const rewards7d = liveRewards7d ?? (boardroomRewardsError ? base.rewards7d : REWARDS_PENDING_USD);
      pinned.push({ ...base, rewards7d });
    } else if (boardroomFallback) {
      // No live row yet — fallback already carries `REWARDS_PENDING_USD`.
      pinned.push(boardroomFallback);
    }

    const nonBoardroom = liveGrids
      .filter(
        (g) =>
          !boardroomLiveId || g.id.toLowerCase() !== boardroomLiveId.toLowerCase(),
      )
      .map((g) => gridSummaryToMockBoard(g, prices));

    // The curated rail surfaces two flavours of boards:
    //
    //   1. anything a curator has explicitly allow-listed
    //      (`g.curated === true`), the long-standing behaviour;
    //   2. anything created within the last
    //      `NEW_BOARD_WINDOW_SECONDS` (4h) — even without a curation
    //      flag — so a freshly-launched grid gets prime placement
    //      while it still has its launch energy and curators have a
    //      window to actually look at it before the rail gets crowded.
    //
    // The `community` bucket then becomes the strict complement of
    // (1) ∪ (2). Without that complement, every fresh board would
    // appear in *both* tabs and the user couldn't tell which list to
    // browse. Once the 4h window lapses on a board that wasn't
    // formally curated, it rolls back into community on the next
    // `nowSec` tick.
    //
    // The check is `b.createdAtSec`-driven (sub-day precision) rather
    // than `b.ageDays`-driven (day-floored) on purpose — see
    // `MockBoard.createdAtSec` for why `ageDays` can't carry this.
    const isCuratedOrFresh = (b: MockBoard): boolean =>
      b.premium || isBoardNew(b, nowSec);

    const curated = nonBoardroom.filter(isCuratedOrFresh);
    const community = nonBoardroom.filter((b) => !isCuratedOrFresh(b));

    const all = [...pinned, ...curated, ...community, ...dev];
    const byId = new Map<string, MockBoard>();
    for (const b of all) byId.set(b.id, b);

    return { pinned, curated, community, dev, byId, all, isLoading, isError };
  }, [liveGrids, isLoading, isError, nowSec, takeoverPriceUsd, ethPrice, boardroomRewards7dRaw, boardroomRewardsError]);
}
